import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import type { RootState, AppDispatch } from '../../../../store';
import {
  getInsightSettings,
  getTimeline,
  TimelineState,
  updateClinicianProfile,
} from '../../../../store/ai2';
import { useUiStore } from '../../../hooks/useUiStore';
import { useObservationEvents } from '../../../hooks';
import type { ClinicianProfile, PatientFeatures, UiStore } from '../types';
import { debounce } from 'lodash';

export const useMedicareTimeline = () => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const access_token = searchParams.get('access_token');

  // Redux state selectors with proper typing
  const { groups, items, startDate, endDate, rawEvents }: TimelineState =
    useSelector((state: RootState) => state.timeline);

  const { selectedTenancy, dashSettings, clinicianId }: ClinicianProfile =
    useSelector((state: RootState) => state.clinicianProfile);

  const { features }: { features: PatientFeatures } = useSelector(
    (state: RootState) => state.patientProfile,
  );

  // Custom hooks
  const { openInsightDetails, closeInsightDetails }: UiStore = useUiStore();
  const results = useObservationEvents();

  // Local state
  const [isInsightEvent, setIsInsightEvent] = useState<boolean>(false);
  const [isActionEvent, setIsActionEvent] = useState<boolean>(false);
  const [forceRefresh, setForceRefresh] = useState<number>(0);

  // Initialize component
  useEffect(() => {
    closeInsightDetails();
  }, []);

  // Fetch timeline data
  useEffect(() => {
    if (access_token) {
      dispatch(
        getTimeline(
          access_token,
          moment().subtract(2, 'year').format(),
          moment().add(0, 'year').format(),
          selectedTenancy,
        ),
      );
      dispatch(getInsightSettings(access_token, selectedTenancy.tenancy_id));
    }
  }, [access_token, forceRefresh, dispatch]);

  // Event handlers
  const insightCloseHandler = (): void => {
    closeInsightDetails();
  };

  const saveDefaultZoom = debounce((offset: number): void => {
    const startDate = moment().subtract(offset, 'milliseconds').format();
    const days = moment().diff(startDate, 'day');
    dispatch(
      updateClinicianProfile({
        timelineZoom: { startValue: days < 7 ? 7 : days, startUnit: 'day' },
      }),
    );
  }, 2000);

  const requestRefresh = (): void => {
    setForceRefresh((prev) => prev + 1);
  };

  return {
    // Timeline data
    groups,
    items,
    rawEvents,
    startDate,
    endDate,

    // Clinician data
    clinicianId,
    dashSettings,

    // Patient data
    features,

    // UI state
    isInsightEvent,
    setIsInsightEvent,
    isActionEvent,
    setIsActionEvent,

    // Event handlers
    openInsightDetails,
    insightCloseHandler,
    saveDefaultZoom,
    requestRefresh,

    // Other data
    results,
  };
};
